import tinycolor from "tinycolor2";
import * as Inflected from "inflected";
import { useRef, useEffect, useState, useCallback } from "react";
const COLORS_WITH_BORDER = ["#ffffff"];
const isDarkModeEnabled = () => document.body.getAttribute("data-theme") === "dark";
const mixColorForTheme = (color, weight = 90, forceDark) => {
  const mixedColor = tinycolor.mix(
    color,
    forceDark || isDarkModeEnabled() ? "#21282e" : "white",
    weight
  );
  return mixedColor.toRgbString();
};
const colorRequiresBorder = (color) => COLORS_WITH_BORDER.some(
  (colorWithBorder) => tinycolor.equals(colorWithBorder, color)
);
const getCssValue = (varName) => {
  return getComputedStyle(document.body).getPropertyValue(varName);
};
const decodeCssVars = (vars) => {
  const cssVarRegex = /(?:var\()?--[\w-]+(?:\))?/g;
  const cssVars = vars.match(cssVarRegex) || [];
  const resolvedVarsMap = /* @__PURE__ */ new Map();
  cssVars.forEach((varName) => {
    if (!resolvedVarsMap.has(varName)) {
      const cleanVarName = varName.replace(/^var\(/, "").replace(/\)$/, "");
      const resolvedValue = getCssValue(cleanVarName);
      resolvedVarsMap.set(varName, resolvedValue);
    }
  });
  let result = vars;
  resolvedVarsMap.forEach((resolvedValue, varName) => {
    if (resolvedValue) {
      const varRegex = new RegExp(
        varName.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"),
        "g"
      );
      result = result.replace(varRegex, resolvedValue);
    }
  });
  return result;
};
const decodeColor = (_color) => {
  let color = _color;
  if (_color.includes("--")) {
    color = decodeCssVars(_color);
  }
  if (color.startsWith("rgba(")) {
    const alpha = 1 - tinycolor(color).getAlpha();
    const hex = tinycolor(color).toHex();
    const mixed = tinycolor.mix(tinycolor(hex), "#ffffff", alpha * 100);
    return mixed.toHexString();
  }
  return tinycolor(color).toHexString();
};
const preventNextKey = ({
  key,
  eventName
}) => {
  const preventNextKeyup = (event) => {
    if (event.key === key) {
      event.preventDefault();
      event.stopPropagation();
      document.removeEventListener(eventName, preventNextKeyup, true);
    }
  };
  document.addEventListener(eventName, preventNextKeyup, true);
};
function pluralize(count, text, locale) {
  return count === 1 ? text : Inflected.pluralize(text, locale);
}
Inflected.inflections("en", (inflect) => {
  inflect.plural(/^sec\.$/i, "secs.");
  inflect.plural(/^min\.$/i, "mins.");
  inflect.plural(/^hr\.$/i, "hrs.");
});
const useEffectAfterMount = (cb, dependencies = []) => {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      return cb();
    }
    mounted.current = true;
  }, dependencies);
};
const getStoredValue = (key, sanitise, session) => {
  const storage = session ? sessionStorage : localStorage;
  const raw = storage.getItem(key);
  if (raw === void 0 || raw === null) {
    return sanitise(void 0);
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn(
      `Invalid JSON in ${session ? "sessionStorage" : "localStorage"} ${key}`,
      key,
      raw
    );
  }
  return sanitise(parsed);
};
const usePersistedState = (key, sanitise, persist = false) => {
  const [value, setValue] = useState(
    () => getStoredValue(key, sanitise, !persist)
  );
  const setValueWrapped = useCallback(
    (val) => {
      const storage = persist ? localStorage : sessionStorage;
      const prevState = getStoredValue(key, sanitise, !persist);
      let updated;
      if (typeof val === "function") {
        updated = val(prevState);
      } else {
        updated = val;
      }
      storage.setItem(key, JSON.stringify(updated));
      setValue(updated);
    },
    [persist, key, sanitise]
  );
  return [value, setValueWrapped];
};
export {
  colorRequiresBorder,
  decodeColor,
  decodeCssVars,
  getCssValue,
  isDarkModeEnabled,
  mixColorForTheme,
  pluralize,
  preventNextKey,
  useEffectAfterMount,
  usePersistedState
};
//# sourceMappingURL=index.js.map
